
import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { checkAdminStatus } from '@/services/admin/adminStatusService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const { toast } = useToast();
  const authCheckAttempts = useRef(0);
  const location = useLocation();
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  
  useEffect(() => {
    console.log(`ProtectedRoute check for path: ${location.pathname}, required role: ${requiredRole || 'none'}`);
    
    const checkAuth = async () => {
      try {
        // Prevent infinite check loops
        if (authCheckAttempts.current > 5) {
          console.error('Too many auth check attempts - preventing infinite loop');
          setIsAuthorized(false);
          setIsLoading(false);
          toast({
            title: 'Authentication Error',
            description: 'Unable to verify your access. Please try logging out and back in.',
            variant: 'destructive',
          });
          return;
        }

        authCheckAttempts.current++;
        console.log(`Protected route check - attempt #${authCheckAttempts.current}, role: ${requiredRole}`);
        
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session fetch error:', sessionError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }
        
        if (!sessionData.session) {
          console.log('No active session found');
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Log current user info for debugging
        console.log('Session user:', sessionData.session.user.email, 'ID:', sessionData.session.user.id);

        // Special handling for admin role
        if (requiredRole === 'admin') {
          setDebugInfo(`Checking admin status for: ${sessionData.session.user.email} (ID: ${sessionData.session.user.id})`);
          const { isAdmin, error } = await checkAdminStatus();
          
          console.log('Admin status check result:', isAdmin);
          setDebugInfo(prev => `${prev || ''}\nAdmin status: ${isAdmin ? 'Yes' : 'No'}`);
          
          if (error) {
            console.error('Admin check error:', error);
            
            // Still attempt to determine admin status by direct query as final fallback
            try {
              const { data: userData } = await supabase
                .from('client_onboarding')
                .select('role')
                .eq('email', sessionData.session.user.email)
                .maybeSingle();
                
              const isAdminByDirect = userData?.role === 'admin';
              console.log('Final fallback admin check result:', isAdminByDirect);
              setDebugInfo(prev => `${prev || ''}\nFallback check: ${isAdminByDirect ? 'Yes' : 'No'}`);
              
              setIsAuthorized(isAdminByDirect);
              setIsLoading(false);
              return;
            } catch (fallbackError) {
              console.error('Fallback admin check failed:', fallbackError);
              setIsAuthorized(false);
              setIsLoading(false);
              return;
            }
          }
          
          // Set authorization based on admin status
          setIsAuthorized(isAdmin);
          setIsLoading(false);
          return;
        }

        // For other roles or no specific role, check client_onboarding
        const { data: userData, error } = await supabase
          .from('client_onboarding')
          .select('status, role')
          .eq('email', sessionData.session.user.email)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user data:', error);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }
        
        console.log('User role data:', userData);
        
        // If no specific role is required, just being authenticated is enough
        if (!requiredRole) {
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }
        
        // Otherwise check if user has the required role
        setIsAuthorized(userData?.role === requiredRole);
        setIsLoading(false);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthorized(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [requiredRole, toast, location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthorized) {
    console.log(`Access denied to ${location.pathname}, redirecting to /auth`);
    return <Navigate to="/auth" replace />;
  }

  console.log(`Access granted to ${location.pathname}`);
  return <>{children}</>;
};

export default ProtectedRoute;
